import { Box, Typography, Stack } from "@mui/material";

import { useEffect, useCallback } from "react";
import { useTranslation } from "react-i18next";

import { useAppDispatch, useAppSelector } from "~/app/store";
import { AutostoreBin } from "~/features/autostoreBin";
import { ErrorAlertBanner } from "~/features/testingTools/ErrorAlertBanner";
import { addEvent } from "~/features/testingTools/testingTools.slice";
import { usePortStatusQuery } from "~/hooks/usePortStatus";
import { getMessageFromRtkError } from "~/lib/rtkErrorToMessage";
import { selectWorkstationIsMultiPortLayout } from "~/redux/selectors/workstationsSelectors";

type PortInheritedProps = {
  portId: number;
  portSideIndex: number;
  portIdToDisplay: number;
};

const Port = ({
  portId,
  portSideIndex,
  portIdToDisplay
}: PortInheritedProps) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation();

  const isMultiPortLayout = useAppSelector(selectWorkstationIsMultiPortLayout);

  const {
    binPortSideIndex,
    binState,
    error: portStatusError,
    activityState,
    refetch,
    ...portStatus
  } = usePortStatusQuery(portId, portSideIndex);
  const selectedBinNumber = portStatus.selectedBin;

  const retryPortStatusFetch = useCallback(async () => {
    await refetch();
  }, [refetch]);

  // Port status errors are logged but don't change session status (only start/stop bin circus errors matter)
  useEffect(() => {
    // eslint-disable-next-line react-you-might-not-need-an-effect/no-event-handler
    if (portStatusError && portSideIndex === 0) {
      dispatch(
        addEvent({
          text: t("getting port status failed for port {{portId}}", {
            portId
          }),
          state: "Failed",
          testOrigin: "Bin to Port"
        })
      );
    }
  }, [dispatch, portStatusError, portId, portSideIndex, t]);

  return (
    <Stack
      sx={{
        flexDirection: "column",
        gap: 2,
        alignItems: "center",
        width: "100%",
        mb: isMultiPortLayout ? 2 : 0,
        ...(isMultiPortLayout && {
          minWidth: 250,
          maxWidth: 350
        }),
        "@media (max-width: 900px)": {
          gap: 1
        },
        ...(!isMultiPortLayout && { minWidth: 250 })
      }}
    >
      <Box
        sx={{
          width: "100%",
          display: "grid",
          gridTemplateRows: "auto 1fr",
          gap: 1,
          maxWidth: isMultiPortLayout ? "100%" : 350,
          minWidth: 250
        }}
      >
        <Typography variant="h5" fontWeight={500} textAlign="center">
          Port {portIdToDisplay}
        </Typography>

        <AutostoreBin
          state={activityState}
          binId={selectedBinNumber}
          pickQuantity={""}
          pickCompartment={null}
          numberOfRows={
            (portSideIndex === binPortSideIndex &&
              binState?.binConfiguration?.horizontalCompartmentCount) ||
            1
          }
          numberOfColumns={
            (portSideIndex === binPortSideIndex &&
              binState?.binConfiguration?.verticalCompartmentCount) ||
            1
          }
        />
      </Box>
      {portStatusError && portSideIndex === 0 && (
        <ErrorAlertBanner
          title={t("getting port status failed for port {{portId}}", {
            portId
          })}
          message={getMessageFromRtkError(portStatusError)}
          onRetry={retryPortStatusFetch}
        />
      )}
    </Stack>
  );
};

export default Port;
